import React from 'react';
import {
  Button,
  ButtonCross,
  Icon,
  Input,
  Link,
  Modal,
} from '../../../../../shared/ui';
import { IconMap } from '../../../../../shared/sprite';
import { object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { fathomModel } from '../../../../../entities/fathom';

type Props = {
  isOpen: boolean;
  onClose(): void;
};

type FathomFormSchema = {
  apiKey: string;
};

const fathomFormInputSchema = object().shape({
  apiKey: string().required('Please enter Fathom Api key'),
});

export const FathomIntegrationModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const dispatch = useDispatch();

  const { register, handleSubmit } = useForm<FathomFormSchema>({
    resolver: yupResolver(fathomFormInputSchema),
  });

  const isConnecting = useSelector(fathomModel.selectors.selectIsConnecting);

  const onSubmit = handleSubmit(({ apiKey }) => {
    dispatch(
      fathomModel.actions.connectToFathom({
        apiKey,
        cb: onClose,
      })
    );
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isShowCancelButton={false}
      className="w-160"
    >
      <div className="flex flex-col gap-4 mb-5">
        <div className="flex items-start justify-between w-full">
          <Icon glyph={IconMap.FathomLg} width={48} />
          <ButtonCross
            onClick={onClose}
            classNames="-translate-y-2 translate-x-2"
          />
        </div>

        <div className="text-display-xs font-display text-gray-900 font-medium">
          Fathom integration
        </div>
      </div>
      <form onSubmit={onSubmit}>
        <p className="text-sm text-gray-600 mb-5">
          Connect your Fathom account to Distribute. All team members will be
          able to use call transcripts.
        </p>
        <p className="text-sm text-gray-600">Simply follow these steps:</p>
        <ol className="text-sm text-gray-600 list-decimal pl-4 py-3 flex flex-col gap-2">
          <li className="">
            Contact{' '}
            <Link
              target="_blank"
              rel="noreferrer"
              href={'https://help.fathom.video/en/contact-us'}
              size="medium"
              className="inline-flex gap-x-0.5 items-center"
            >
              Fathom support
              <Icon glyph={IconMap.ArrowSquareUpRight} width={16} />
            </Link>{' '}
            to enable API key access for your account.
          </li>
          <li>
            Once access is granted, go to your{' '}
            <span className="font-semibold text-gray-900">
              Fathom Account Settings.
            </span>
          </li>
          <li>
            Under the Integrations section, find your{' '}
            <span className="font-semibold text-gray-900">API key</span> and
            copy it.
          </li>
          <li>
            Paste the key below and click{' '}
            <span className="font-semibold text-gray-900">“Connect”</span> to
            complete the connection.
          </li>
        </ol>
        <div className="flex flex-col gap-3 mb-5">
          <Input
            type="text"
            placeholder="API Key"
            autoComplete="off"
            heightSize="md"
            {...register('apiKey')}
          />
        </div>
        <Button
          type="submit"
          variant="text"
          color="primary"
          className="w-25"
          loading={isConnecting}
        >
          Connect
        </Button>
      </form>
    </Modal>
  );
};
